import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts'
import type { DataZoomComponentOption, GridComponentOption, LegendComponentOption, ToolboxComponentOption, TooltipComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'

import * as zod from 'zod'
import type { DateFormat } from '../PersonalSettings'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR, defaultInterval, IntervalLockSupportSchema, IntervalSupportSchema, MarkableWidgetSchema, MULTIGRID_CHART, TooltipCompareTypeSchema, WidgetElementBufferSupportSchema, WidgetElementSchema, WidgetSchema, WidgetSerieTypeSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type IntervalSupport, type MarkableWidget, type PartialWidget, type PartialWidgetElement, type TooltipCompareType, type WidgetBufferValue, type WidgetConstructor, type WidgetElementConstructor, type WidgetSerieType } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'
import type { DepartmentPageLine } from '../Device'
import type { SetRequiredDeep } from 'type-fest'

export type MultigridChartOptions = ComposeOption<
  LineSeriesOption
  | BarSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | ToolboxComponentOption
  | DataZoomComponentOption
  | LegendComponentOption
>

export type MultigridChartWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'MULTIGRID_CHART'
  widget: MultigridChartWidget
  dateFormat: DateFormat
}

export type MultigridChartWidgetOptionsResponse = WidgetDataResponse & {
  type: 'MULTIGRID_CHART'
  options: MultigridChartOptions
  compareTypesTooltip: Map<string, TooltipCompareType>
}

export const MultigridChartWidgetElementSchema = WidgetElementSchema.merge(WidgetElementBufferSupportSchema).extend({
  type: MULTIGRID_CHART,
  color: zod.string().nullable(),
  showAverageValue: zod.boolean(),
  showLowestValue: zod.boolean(),
  showHighestValue: zod.boolean(),
  serieType: WidgetSerieTypeSchema,
  compareTypeTooltip: TooltipCompareTypeSchema,
})

export type MultigridChartWidgetElement = zod.infer<typeof MultigridChartWidgetElementSchema>
export type MultigridChartWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<MultigridChartWidgetElement>, 'type'>

export const MultigridChartWidgetSchema = WidgetSchema.merge(MarkableWidgetSchema).merge(IntervalSupportSchema).merge(IntervalLockSupportSchema).extend({
  type: MULTIGRID_CHART,
  elements: MultigridChartWidgetElementSchema.array(),
})
export type MultigridChartWidget = zod.infer<typeof MultigridChartWidgetSchema>
export type MultigridChartWidgetPartial = SetRequiredDeep<PartialWidget<MultigridChartWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface MultigridChartWidgetElementData extends GraphWidgetElementData {
  showAverageValue: boolean
  showLowestValue: boolean
  showHighestValue: boolean
  serieType: WidgetSerieType
  compareTypeTooltip: TooltipCompareType
  values: WidgetBufferValue[]
  compareToValues: WidgetBufferValue[]
  availableUnits: Record<string, string>
}
export interface MultigridChartWidgetData extends AbstractWidgetData<MultigridChartWidgetElementData>, MarkableWidget, IntervalSupport {
  type: 'MULTIGRID_CHART'
  elements: MultigridChartWidgetElementData[]
  timestamps: string[]
  compareToTimestamps: string[]
}
export interface MultigridChartWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  compareEnabled: boolean
  type: 'MULTIGRID_CHART'
  line?: DepartmentPageLine
  allowBufferCreation: boolean
}

export const DEFAULT_MULTIGRID_CHART_WIDGET_ELEMENT_CONSTRUCTOR: WidgetElementConstructor<MultigridChartWidgetElementPartial> = () => ({
  ...COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR(),
  pcNumber: null,
  useDefaultPcNumber: true,
  color: null,
  showAverageValue: false,
  showHighestValue: false,
  showLowestValue: false,
  serieType: 'line',
  compareTypeTooltip: 'NONE',
  type: 'MULTIGRID_CHART',
  buffer: null,
})

export const MULTIGRID_CHART_WIDGET_CONSTRUCTOR: WidgetConstructor<MultigridChartWidgetPartial> = () => ({
  type: 'MULTIGRID_CHART',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  interval: defaultInterval(),
  markAreaElements: [],
  elements: [DEFAULT_MULTIGRID_CHART_WIDGET_ELEMENT_CONSTRUCTOR()],
})
